import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import therapistApi from '../../services/therapistApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const ReportConfigurationModal = ({ open, onClose, clientId, clientName, isSynthetic = false }) => {
  // State for report configuration
  const [config, setConfig] = useState({
    clientCode: '',
    format: 'pdf',
    includeConversations: true,
    includeAssessments: true,
    includeGoals: true,
    includeCheckIns: true,
    includeJournalEntries: true,
    includeStatistics: true,
    generateSummaries: false,
    generateReviews: false
  });

  // State for prerequisites check
  const [prerequisites, setPrerequisites] = useState(null);
  const [checkingPrereqs, setCheckingPrereqs] = useState(false);
  const [generatingSummaries, setGeneratingSummaries] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Check prerequisites when modal opens. Skipped for synthetic clients —
  // the modal short-circuits to a "not available" message instead.
  useEffect(() => {
    if (open && clientId && !isSynthetic) {
      checkPrerequisites();
    }
  }, [open, clientId, isSynthetic]);

  const checkPrerequisites = async () => {
    setCheckingPrereqs(true);
    setError(null);
    try {
      const response = await therapistApi.checkReportPrerequisites(clientId);
      setPrerequisites(response);
    } catch (error) {
      console.error('Error checking prerequisites:', error);
      setError('Failed to check report prerequisites');
    } finally {
      setCheckingPrereqs(false);
    }
  };

  const handleGenerateSummaries = async () => {
    setGeneratingSummaries(true);
    setError(null);
    try {
      await therapistApi.generateMissingSummaries(clientId);
      // Recheck prerequisites
      await checkPrerequisites();
    } catch (error) {
      console.error('Error generating summaries:', error);
      setError('Failed to generate summaries');
    } finally {
      setGeneratingSummaries(false);
    }
  };

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = async () => {
    if (isSynthetic) {
      setError('Reports are not available for training clients.');
      return;
    }
    if (!config.clientCode.trim()) {
      setError('Please enter a client code for identification');
      return;
    }

    setDownloading(true);
    setError(null);
    
    try {
      const response = await therapistApi.generateReport(clientId, config);
      
      // Handle different formats
      if (config.format === 'pdf') {
        // Create blob from response data
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `client-report-${config.clientCode}-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'markdown') {
        // Handle markdown download
        const blob = new Blob([response.data], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `client-report-${config.clientCode}-${Date.now()}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle JSON - could display in a new window or download
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `client-report-${config.clientCode}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setDownloading(false);
    }
  };

  const renderPrerequisiteStatus = () => {
    if (checkingPrereqs) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">Checking prerequisites...</Typography>
        </Box>
      );
    }

    if (!prerequisites) return null;

    // Handle both response.data and direct response structures
    const prereqData = prerequisites.data || prerequisites;
    const { ready, stats } = prereqData;

    if (ready) {
      return (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          All prerequisites met. Report can be generated.
        </Alert>
      );
    }

    return (
      <Alert 
        severity="warning" 
        icon={<WarningIcon />}
        action={
          <Button 
            size="small" 
            onClick={handleGenerateSummaries}
            disabled={generatingSummaries}
          >
            {generatingSummaries ? 'Generating...' : 'Generate Now'}
          </Button>
        }
      >
        <Typography variant="body2" gutterBottom>
          {stats?.conversationsNeedingSummary?.length || 0} conversation(s) need summaries before report generation.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click "Generate Now" to create missing summaries automatically.
        </Typography>
      </Alert>
    );
  };

  // Synthetic-clients-in-therapist-ui (Phase 2): reports are not available
  // for training/practice clients in v1 (the watermarked practice-transcript
  // export is deferred to v2). This short-circuit is belt-and-braces — the
  // parent ClientDetailView already hides/disables the trigger.
  if (isSynthetic) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Typography variant="h5" gutterBottom>
            Generate Client Report
          </Typography>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Reports are not available for training clients.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This is a synthetic practice client. Clinical exports (PDF, Markdown, JSON)
            are intentionally disabled so practice data cannot be mistaken for a real
            clinical record.
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={onClose} startIcon={<DownloadIcon />} disabled>
              Generate Report
            </Button>
            <Button onClick={onClose} sx={{ ml: 1 }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" gutterBottom>
          Generate Client Report
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Generate a comprehensive report for {clientName || 'this client'}.
          Personal information (name, email) will be excluded for privacy.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Prerequisites Check */}
        {renderPrerequisiteStatus()}

        {/* Client Code Input */}
        <TextField
          fullWidth
          label="Client Identification Code"
          value={config.clientCode}
          onChange={handleConfigChange('clientCode')}
          margin="normal"
          required
          helperText="Enter a code to identify this client in the report (e.g., initials, case number)"
          error={!config.clientCode.trim() && downloading}
        />

        {/* Format Selection */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Report Format</InputLabel>
          <Select
            value={config.format}
            onChange={handleConfigChange('format')}
            label="Report Format"
          >
            <MenuItem value="pdf">PDF Document</MenuItem>
            <MenuItem value="markdown">Markdown Text</MenuItem>
            <MenuItem value="json">JSON Data</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Report Sections */}
        <Typography variant="subtitle1" gutterBottom>
          Report Sections
        </Typography>
        
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeConversations}
                onChange={handleConfigChange('includeConversations')}
              />
            }
            label="Conversations & Messages"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeAssessments}
                onChange={handleConfigChange('includeAssessments')}
              />
            }
            label="Assessment Results"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeGoals}
                onChange={handleConfigChange('includeGoals')}
              />
            }
            label="Goals & Progress"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeCheckIns}
                onChange={handleConfigChange('includeCheckIns')}
              />
            }
            label="Check-In Data"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeJournalEntries}
                onChange={handleConfigChange('includeJournalEntries')}
              />
            }
            label="Journal Entries"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeStatistics}
                onChange={handleConfigChange('includeStatistics')}
              />
            }
            label="Usage Statistics"
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Advanced Options */}
        <Typography variant="subtitle1" gutterBottom>
          Advanced Options
        </Typography>
        
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.generateSummaries}
                onChange={handleConfigChange('generateSummaries')}
              />
            }
            label="Auto-generate missing summaries"
            disabled={!config.includeConversations}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.generateReviews}
                onChange={handleConfigChange('generateReviews')}
              />
            }
            label="Include AI performance reviews"
            disabled={!config.includeConversations}
          />
        </Stack>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button onClick={onClose} disabled={downloading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={downloading || checkingPrereqs || !config.clientCode.trim()}
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {downloading ? 'Generating...' : 'Generate Report'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReportConfigurationModal;